import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { submitFormSession, fetchFormSessionProfile, getFormSessionErrorMessage, firstIncompleteStepIndex, FORMS_API_URL } from '../utils/formSessionApi';
import {
  isDaysOrIsoDate,
  isEarnestMoneyValue,
  isHomeWarrantyBuyerRequestValue,
  isPositiveIntegerDays,
  isPropertyAddressComplete,
  isSigningDateValue,
  normalizeStateCode,
} from '../utils/wizardFieldValidation';
import {
  missingPropertyAddressReason,
  wizardBlockedReason,
  WIZARD_ANSWER_QUESTION_REASON,
  WIZARD_PRIMARY_BUTTON_CLASS,
} from '../utils/wizardContinue';
import OpposingAgentInfoFields from './OpposingAgentInfoFields';
import WizardClientPicker, { resetDealLinkFields } from './WizardClientPicker';
import { applyPaWizardDefaults } from '../utils/paWizardDefaults';
import { purchaseAgreementCloseResult } from '../utils/dashboardFormLaunch';
import './ContractWizard.css';
import { getContractApiUrl } from '../utils/apiUrls';
import { trackFormWizardEvent, formWizardEventPayload } from '../utils/formWizardTelemetry';
import WizardProgress from './WizardProgress';
import WizardStepReady from './WizardStepReady';
import PropertyAddressFields from './PropertyAddressFields';
import { isRealEstateAgentUserType } from '../constants/userTypes';

// Backend URL configuration
const BACKEND_URL = getContractApiUrl();

const formatWizardPurchasePrice = (value) => {
  const numeric = Number(String(value).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(numeric)) {
    return String(value);
  }
  return `$${Math.round(numeric).toLocaleString('en-US')}`;
};

const WIZARD_STEP_ID_ALIASES = {
  buyer_name: 'wizard_client_buyer',
  seller_name: 'wizard_client_seller',
};

const resolveWizardQuestionIndex = (questions, stepId) => {
  if (!stepId || !questions?.length) return -1;
  const normalized = WIZARD_STEP_ID_ALIASES[stepId] || stepId;
  return questions.findIndex((question) => question.id === normalized);
};

const resolveWizardStartStep = (questions, steps, seededValues) => {
  if (!questions?.length) return 0;
  if (!steps?.length) {
    const contractTypeIdx = resolveWizardQuestionIndex(questions, 'contract_type');
    return contractTypeIdx >= 0 ? contractTypeIdx : 0;
  }
  const stepIdx = firstIncompleteStepIndex(steps, seededValues);
  const stepId = steps[stepIdx]?.step_id;
  const questionIdx = resolveWizardQuestionIndex(questions, stepId);
  return questionIdx >= 0 ? questionIdx : 0;
};

const NEVER_AUTO_SKIP_STEP_IDS = new Set([
  'contract_type',
  'creating_agent_side',
  'wizard_client_buyer',
  'wizard_client_seller',
  'opposing_client_buyer',
  'late_seller_name',
  'property_address',
  'legal_description',
  'purchase_price',
  'survey_mortgage_payer',
  'home_warranty_buyer_request',
]);

/** Stable empty default — avoids re-seeding wizard state on every parent re-render. */
const EMPTY_INITIAL_VALUES = Object.freeze({});

const CONTRACT_TYPE_OPTIONS = [
  { value: 'residential_home', label: 'Residential Home' },
  { value: 'farm_and_ranch', label: 'Farm, Ranch, or Recreational Land' },
  { value: 'vacant_land', label: 'Vacant Land' },
  { value: 'commercial', label: 'Commercial' },
];

const UNSUPPORTED_CONTRACT_TYPE_MESSAGE =
  'KeyFlo does not currently support automatically generating this type of purchase agreement. '
  + 'However, KeyFlo can process and manage transactions as your TC for other types of property sales.';

const localDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Match backend: calendar-day offset, then roll forward off weekends (holidays adjusted on submit). */
const adjustToNextBusinessDayPreview = (dateStr) => {
  const date = new Date(`${dateStr}T12:00:00`);
  while (date.getDay() === 0 || date.getDay() === 6) {
    date.setDate(date.getDate() + 1);
  }
  return localDateString(date);
};

const addCalendarDaysPreview = (dateStr, days) => {
  const date = new Date(`${dateStr}T12:00:00`);
  date.setDate(date.getDate() + days);
  return localDateString(date);
};

/** e.g. 2026-07-17 → July 17, 2026 */
const formatLongDate = (isoStr) => {
  if (!isoStr) return '';
  const date = new Date(`${isoStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return isoStr;
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const agentFieldPrefix = (side) => (side === 'buyer' ? 'buyer_agent' : 'sellers_agent');

const VALID_AGENT_SIDES = new Set(['buyer', 'seller', 'dual']);

const hasCreatingAgentSide = (data) => VALID_AGENT_SIDES.has(data?.creating_agent_side);

const isBuyerSideOnly = (data) => data?.creating_agent_side === 'buyer';
const isSellerSideOnly = (data) => data?.creating_agent_side === 'seller';
const isDualAgency = (data) => data?.creating_agent_side === 'dual';

const ACD_PROPERTY_CONDITION_OPTIONS = [
  { value: 'rpcd', label: 'RPCD received (standard disclosure statement)' },
  { value: 'disclaimer', label: 'Disclaimer statement received' },
  { value: 'exempt_838', label: 'Exempt under Title 60 §838' },
  { value: 'not_required', label: 'Disclosure not required under RPCD Act' },
];

const ACD_LBP_OPTIONS = [
  { value: 'lbp_disclosure', label: 'Pre-1978 — LBP disclosure & pamphlet received' },
  { value: 'post_1978', label: 'Built 1978 or later — LBP exempt' },
  { value: 'non_residential', label: 'Not a residential dwelling — LBP N/A' },
];

const suggestedAcdLbpStatus = (data) => {
  const year = parseInt(data?.year_built, 10);
  if (Number.isNaN(year)) return null;
  return year <= 1977 ? 'lbp_disclosure' : 'post_1978';
};

const needsLegalDescriptionStep = (data) => (
  isBuyerSideOnly(data) || isDualAgency(data)
);

const needsPropertyStep = (data) => (
  !data?.listing_id
  && !isPropertyAddressComplete(data, data?.property_address_state)
  && (isBuyerSideOnly(data) || isSellerSideOnly(data) || isDualAgency(data))
);

const needsCreatingAgentSideChoice = (data) => (
  Boolean(data?.needs_creating_agent_side_choice) && !hasCreatingAgentSide(data)
);

const applyCreatingAgentProfileFromUser = (data, side, profile) => {
  if (!profile || !side || !isRealEstateAgentUserType(profile.user_type)) return data;
  const prefix = agentFieldPrefix(side);
  const profileKey = side === 'buyer' ? 'buyer_agent_profile' : 'agent_profile';
  const next = { ...data };
  const nestedProfile = {
    ...(next[profileKey] || {}),
    name: profile.name,
    license_number: profile.license_number,
    email: profile.email,
    phone_number: profile.phone_number,
    brokerage_company_name: profile.brokerage_company_name,
    company_license_number: profile.company_license_number,
    company_address: profile.company_address,
    brokerage_phone: profile.phone_number,
  };
  next[profileKey] = nestedProfile;

  const gapFields = {
    [`${prefix}_name`]: profile.name,
    [`${prefix}_license`]: profile.license_number,
    [`${prefix}_email`]: profile.email,
    [`${prefix}_phone`]: profile.phone_number,
    [`${prefix}_brokerage`]: profile.brokerage_company_name,
    [`${prefix}_brokerage_license`]: profile.company_license_number,
    [`${prefix}_brokerage_address`]: profile.company_address,
    [`${prefix}_brokerage_phone`]: profile.phone_number,
  };
  Object.entries(gapFields).forEach(([key, val]) => {
    if (val && !next[key]) next[key] = val;
  });

  const opposingSide = side === 'seller' ? 'buyer' : 'seller';
  if (next.creating_agent_side !== 'dual' && !isAgentSideComplete(next, opposingSide)) {
    next.needs_opposing_agent_info = true;
    next.opposing_agent_side = opposingSide;
  }
  return next;
};

const isAgentSideComplete = (data, side) => {
  const prefix = agentFieldPrefix(side);
  const keys = ['name', 'license', 'email', 'phone', 'brokerage', 'brokerage_license', 'brokerage_address', 'brokerage_phone'];
  return keys.every((key) => {
    const val = data[`${prefix}_${key}`];
    return val && String(val).trim().length > 0;
  });
};

const getQuestionText = (question, contractData) => {
  if (typeof question.question === 'function') {
    return question.question(contractData);
  }
  return question.question;
};

const getQuestionFieldKey = (question) => question.dataKey || question.id;

const isStepComplete = (question, contractData) => {
  if (question.validateStep) {
    return question.validateStep(contractData);
  }
  if (question.validation) {
    return question.validation(contractData[getQuestionFieldKey(question)]);
  }
  return Boolean(contractData[getQuestionFieldKey(question)]);
};

const isQuestionVisible = (question, data) => (
  !question.showCondition || question.showCondition(data)
);

const CONTRACT_SELECT_REASONS = {
  contract_type: 'Select a purchase agreement type to continue.',
  creating_agent_side: 'Select who you represent to continue.',
  wizard_client_buyer: 'Select a buyer to continue.',
  wizard_client_seller: 'Select a listing to continue.',
  opposing_client_buyer: 'Select a buyer to continue.',
};

const contractContinueBlockedReason = (question, data, { ready, licensedState } = {}) => {
  if (ready) return '';
  if (question?.id === 'contract_type' && data?.contract_type && data.contract_type !== 'residential_home') {
    return UNSUPPORTED_CONTRACT_TYPE_MESSAGE;
  }
  if (question?.id === 'property_address') {
    return missingPropertyAddressReason(data, licensedState);
  }
  if (CONTRACT_SELECT_REASONS[question?.id]) return CONTRACT_SELECT_REASONS[question.id];
  return wizardBlockedReason({ ready: false, fallback: WIZARD_ANSWER_QUESTION_REASON });
};

/** Clear dependent answers when a parent conditional field changes. */
const applyWizardAnswer = (prev, fieldId, value) => {
  const next = { ...prev, [fieldId]: value };
  if (fieldId === 'mortgage_survey_report') {
    delete next.survey_mortgage_payer;
  }
  if (fieldId === 'home_warranty_seller' && value === 'yes') {
    delete next.home_warranty_buyer_request;
  }
  return next;
};

/**
 * Shared purchase-agreement wizard used by the KeyFlo portal and magic-link form sessions.
 *
 * - formSessionToken set → submit via /forms/sessions/:token/submit
 * - no formSessionToken → portal create (POST /create-new-contract)
 * - initialValues / wizardSteps → email prefill (magic link only)
 */
const ContractWizard = ({
  isOpen,
  onClose,
  onBackToCatalog,
  mode = 'modal',
  initialValues = EMPTY_INITIAL_VALUES,
  wizardSteps = [],
  formSessionToken = null,
  initialSubmitResult = null,
}) => {
  const { user: auth0User, getAccessTokenSilently } = useAuth0();
  const isPageMode = mode === 'page';
  const visible = isPageMode || isOpen;
  const [currentStep, setCurrentStep] = useState(0);
  const [contractData, setContractData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(initialSubmitResult || null);
  const [userProfile, setUserProfile] = useState(null);
  const [stepSeeded, setStepSeeded] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [contractTypeError, setContractTypeError] = useState('');
  const [propertyEnriching, setPropertyEnriching] = useState(false);
  const wizardOpenedRef = useRef(false);
  const wizardStartStepResolvedRef = useRef(false);
  const initialValuesRef = useRef(initialValues);
  const wizardStepsRef = useRef(wizardSteps);
  initialValuesRef.current = initialValues;
  wizardStepsRef.current = wizardSteps;

  // Fetch user profile when wizard opens & fire 'started' event
  useEffect(() => {
    if (!visible) return;
    if (formSessionToken) {
      fetchFormSessionProfile(formSessionToken)
        .then(setUserProfile)
        .catch(console.error)
        .finally(() => setProfileReady(true));
    } else if (auth0User?.email) {
      fetchUserProfile();
      trackFormWizardEvent(getAccessTokenSilently, formWizardEventPayload({
        phase: 'started',
        formType: 'purchase_agreement',
        userEmail: auth0User.email,
        userName: auth0User.name || auth0User.nickname || '',
      })).catch(() => { });
    } else {
      setProfileReady(true);
    }
  }, [visible, auth0User, formSessionToken, getAccessTokenSilently]);

  // Seed prefill once when the wizard opens; do not reset on parent re-renders.
  // (Start step is set in a later effect once `questions` is defined.)
  useEffect(() => {
    if (!visible) {
      wizardOpenedRef.current = false;
      wizardStartStepResolvedRef.current = false;
      return;
    }
    if (wizardOpenedRef.current) return;
    wizardOpenedRef.current = true;
    const seededValues = applyPaWizardDefaults({
      contract_type: 'residential_home',
      ...initialValuesRef.current,
    });
    setContractData(seededValues);
    setStepSeeded(true);
    setContractTypeError('');
  }, [visible]);

  useEffect(() => {
    if (!userProfile?.licensed_state) return;
    setContractData((prev) => {
      if ((prev.property_address_state || '').trim()) return prev;
      const normalized = normalizeStateCode(userProfile.licensed_state);
      if (!normalized) return prev;
      return { ...prev, property_address_state: normalized };
    });
  }, [userProfile?.licensed_state]);

  useEffect(() => {
    if (!userProfile) return;
    setContractData((prev) => {
      if (!hasCreatingAgentSide(prev)) return prev;
      if (isDualAgency(prev)) {
        let updated = applyCreatingAgentProfileFromUser(prev, 'buyer', userProfile);
        updated = applyCreatingAgentProfileFromUser(updated, 'seller', userProfile);
        updated.needs_opposing_agent_info = false;
        return updated;
      }
      return applyCreatingAgentProfileFromUser(prev, prev.creating_agent_side, userProfile);
    });
  }, [userProfile]);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/user/profile?email=${encodeURIComponent(auth0User.email)}`);
      if (response.ok) {
        const profileData = await response.json();
        setUserProfile(profileData);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setProfileReady(true);
    }
  };

  // Calculate derived dates
  const getEffectiveDate = () => localDateString(new Date());

  const getStandardClosingDate = () => {
    const raw = addCalendarDaysPreview(getEffectiveDate(), 30);
    return adjustToNextBusinessDayPreview(raw);
  };

  const getStandardContingencyStartDate = () => {
    const raw = addCalendarDaysPreview(getEffectiveDate(), 3);
    return adjustToNextBusinessDayPreview(raw);
  };

  /** Skip hidden conditional steps or already-answered steps (email prefill). */
  const resolveNextStepIndex = (fromStep, dataSnapshot = contractData) => {
    let nextStep = fromStep + 1;
    while (nextStep < questions.length) {
      const nextQuestion = questions[nextStep];
      if (!isQuestionVisible(nextQuestion, dataSnapshot)) {
        nextStep += 1;
        continue;
      }
      if (
        !NEVER_AUTO_SKIP_STEP_IDS.has(nextQuestion.id)
        && !nextQuestion.showCondition
        && nextQuestion.required
        && isStepComplete(nextQuestion, dataSnapshot)
        && dataSnapshot[getQuestionFieldKey(nextQuestion)]
      ) {
        nextStep += 1;
        continue;
      }
      break;
    }
    return Math.min(nextStep, questions.length - 1);
  };

  /** Save an answer and advance using the updated snapshot (avoids stale conditional skips). */
  const commitAnswerAndAdvance = (fieldId, value) => {
    const fromStep = currentStep;
    setContractData((prev) => {
      const updated = applyWizardAnswer(prev, fieldId, value);
      if (fromStep < questions.length - 1) {
        setCurrentStep(resolveNextStepIndex(fromStep, updated));
      }
      return updated;
    });
  };

  const getEarnestMoneyAmount = () => {
    const purchasePrice = parseFloat(contractData.purchase_price) || 0;
    const earnestMoney = purchasePrice * 0.03;
    return earnestMoney.toFixed(0); // Return as string without decimals
  };

  // Helper function to get visible questions
  const getVisibleQuestions = () => (
    questions.filter((question) => isQuestionVisible(question, contractData))
  );

  const questions = [
    {
      id: 'contract_type',
      question: 'What type of purchase agreement are you creating?',
      type: 'contract_type',
      required: true,
      validateStep: (data) => data.contract_type === 'residential_home',
    },
    {
      id: 'creating_agent_side',
      question: 'Are you representing the Buyer, Seller or both for this transaction?',
      type: 'creating_agent_side',
      required: true,
      validateStep: (data) => hasCreatingAgentSide(data),
    },
    {
      id: 'wizard_client_buyer',
      question: 'Which buyer client is this purchase agreement for?',
      type: 'wizard_client',
      clientSide: 'buyer',
      required: true,
      showCondition: (data) => isBuyerSideOnly(data) || isDualAgency(data),
      validateStep: (data) => Boolean(data.buyer_agreement_id) && (data.buyer_name || '').trim().length > 0,
    },
    {
      id: 'wizard_client_seller',
      question: 'Which listing is this purchase agreement for?',
      type: 'wizard_client',
      clientSide: 'seller',
      required: true,
      showCondition: (data) => isSellerSideOnly(data) || isDualAgency(data),
      validateStep: (data) => Boolean(data.listing_id) && (data.seller_name || '').trim().length > 0,
    },
    {
      id: 'property_address',
      question: 'What is the property address?',
      type: 'address',
      placeholder: 'Enter street number and street name (e.g., 123 Main St)',
      cityPlaceholder: 'Enter city (e.g., Oklahoma City)',
      statePlaceholder: 'State (e.g., OK)',
      zipPlaceholder: 'ZIP code (e.g., 73107)',
      required: true,
      showCondition: (data) => needsPropertyStep(data),
      validateStep: (data) => isPropertyAddressComplete(data, userProfile?.licensed_state),
    },
    {
      id: 'legal_description',
      question: 'What is the legal description of the property (Section 1)?',
      type: 'textarea',
      placeholder: 'Lot, block, subdivision, plat book/page, metes and bounds, etc.',
      required: true,
      showCondition: (data) => needsLegalDescriptionStep(data),
      validateStep: (data) => (data.legal_description || '').trim().length > 0,
    },
    {
      id: 'acd_property_condition',
      question: 'Which Residential Property Condition Disclosure option applies on the Acknowledgement of Disclosures cover page?',
      type: 'acd_property_condition',
      required: true,
      showCondition: (data) => data.contract_type === 'residential_home',
      validateStep: (data) => Boolean(data.acd_property_condition),
    },
    {
      id: 'acd_lbp_status',
      question: (data) => {
        const yearBuilt = parseInt(data.year_built, 10);
        if (!Number.isNaN(yearBuilt)) {
          return `Lead-based paint disclosure for this ${yearBuilt} home — which acknowledgement applies on the cover page?`;
        }
        return 'Which lead-based paint disclosure acknowledgement applies on the cover page?';
      },
      type: 'acd_lbp_status',
      required: true,
      showCondition: (data) => data.contract_type === 'residential_home',
      validateStep: (data) => Boolean(data.acd_lbp_status),
    },
    {
      id: 'purchase_price',
      question: 'What is the offered purchase price?',
      type: 'currency',
      placeholder: 'Enter dollar amount (e.g., $500,000)',
      required: true,
      validation: (value) => value && !isNaN(value) && parseFloat(value) > 0
    },
    {
      id: 'earnest_money_amount',
      question: `The standard earnest money amount is 3%. \nThat is $${contractData.purchase_price ? (parseFloat(contractData.purchase_price) * 0.03).toLocaleString() : '0'} for this offer. \n\nWhat amount should the earnest money be?`,
      type: 'earnest_money',
      placeholder: 'Enter dollar amount or percentage (e.g., 15000 or 3%)',
      required: true,
      validation: (value) => value && isEarnestMoneyValue(value, contractData.purchase_price)
    },
    {
      id: 'title_company_name',
      question: 'Who is the escrow holder for this transaction?',
      type: 'text',
      placeholder: 'Enter name of title company',
      required: true,
      validation: (value) => value && value.trim().length > 0
    },
    {
      id: 'closing_days',
      question: `Please select the standard 30 days to closing - ${formatLongDate(getStandardClosingDate())} - or provide the alternative date or number of days to close desired.`,
      type: 'closing_days',
      placeholder: `e.g. ${formatLongDate(getStandardClosingDate())} or 45`,
      required: true,
      validation: (value) => value && isDaysOrIsoDate(value)
    },
    {
      id: 'property_inclusions',
      question: 'Are there any non-standard property inclusions to remain with the property?',
      type: 'inclusions_exclusions',
      placeholder: 'Enter any special inclusions',
      required: true
    },
    {
      id: 'property_exclusions',
      question: 'Are there any exclusions (items that will NOT remain with the property)?',
      type: 'inclusions_exclusions',
      placeholder: 'Enter any exclusions',
      required: true
    },
    {
      id: 'contingency_start_days',
      question: 'The standard start date of the contingency period is 3 days after the contract is fully executed. Please select 3 days or provide an alternative date.',
      type: 'contingency_start',
      placeholder: `e.g. ${formatLongDate(getStandardContingencyStartDate())} or 3 days`,
      required: true,
      validation: (value) => value && isDaysOrIsoDate(value)
    },
    {
      id: 'inspection_days',
      question: 'The standard investigation and inspection period is 10 days. How many days should the inspection contingency period be?',
      type: 'inspection_days',
      placeholder: 'Enter number of days',
      required: true,
      validation: (value) => value && isPositiveIntegerDays(value)
    },
    {
      id: 'inspection_access_persons',
      question: 'Are there any non-standard persons that need to access the Property during the inspection period?',
      type: 'access_persons',
      placeholder: 'Enter names of persons who need access',
      required: true
    },
    {
      id: 'negotiation_days',
      question: 'The standard negotiation period for any Treatment, Repair or Replacement items after the delivery of the TRR form is 7 days. How many days should the negotiation period be?',
      type: 'negotiation_days',
      placeholder: 'Enter number of days',
      required: true,
      validation: (value) => value && isPositiveIntegerDays(value)
    },
    {
      id: 'title_insurance',
      question: 'Will the Buyer have a title insurance policy?',
      type: 'yesno',
      required: true
    },
    {
      id: 'mortgage_survey_report',
      question: 'Will the buyer provide a Mortgage Inspection Report or a Boundary Survey or neither?',
      type: 'mortgage_survey_report',
      options: ['mortgage report', 'survey report', 'no report'],
      required: true
    },
    {
      id: 'survey_mortgage_payer',
      question: (data) => {
        if (data.mortgage_survey_report === 'mortgage report') {
          return 'Will the Buyer or Seller pay for the Mortgage Inspection?';
        }
        if (data.mortgage_survey_report === 'survey report') {
          return 'Will the Buyer or Seller pay for the Boundary Survey?';
        }
        return 'Will the Buyer or Seller pay for the Survey/Mortgage Inspection?';
      },
      type: 'survey_mortgage_payer',
      options: ['Buyer', 'Seller'],
      required: true,
      showCondition: (data) => data.mortgage_survey_report === 'mortgage report'
        || data.mortgage_survey_report === 'survey report'
    },
    {
      id: 'title_extension_days',
      question: 'The standard closing extension in the event there are title issues is 30 days. How many days should closing be extended in this event?',
      type: 'title_extension',
      placeholder: 'Enter number of days',
      required: true,
      validation: (value) => value && isPositiveIntegerDays(value)
    },
    {
      id: 'home_warranty_seller',
      question: 'Has the Seller committed to a Residential Service Agreement (Home warranty)?',
      type: 'yesno',
      required: true
    },
    {
      id: 'home_warranty_buyer_request',
      question: 'Is the Buyer requesting Home Warranty, and if yes, how much are they requesting is paid by the Seller?',
      type: 'home_warranty_buyer_request',
      placeholder: 'Enter dollar amount if requesting home warranty paid by Seller',
      required: true,
      showCondition: (data) => data.home_warranty_seller === 'no',
      validation: (value) => isHomeWarrantyBuyerRequestValue(value),
    },
    {
      id: 'additional_provisions',
      question: 'Are there any additional provisions that the Buyer would like to place on this Purchase Agreement?',
      type: 'additional_provisions',
      placeholder: 'Enter any additional provisions',
      required: true
    },
    {
      id: 'opposing_client_buyer',
      dataKey: 'buyer_name',
      question: 'Who is the buyer?',
      type: 'text',
      placeholder: 'Enter buyer name(s) or organization',
      required: true,
      showCondition: (data) => isSellerSideOnly(data),
      validateStep: (data) => (data.buyer_name || '').trim().length > 0,
    },
    {
      id: 'buyer_agent_info',
      question: "Please provide the Buyer's Agent information. Start typing a name to pick from agents you've worked with before.",
      type: 'agent_info',
      side: 'buyer',
      required: true,
      showCondition: (data) => (
        isSellerSideOnly(data)
        && (data.buyer_name || '').trim().length > 0
        && data.needs_opposing_agent_info
      ),
      validateStep: (data) => isAgentSideComplete(data, 'buyer'),
    },
    {
      id: 'late_seller_name',
      dataKey: 'seller_name',
      question: 'Who is the seller?',
      type: 'text',
      placeholder: 'Enter seller name(s) or organization',
      required: true,
      showCondition: (data) => isBuyerSideOnly(data),
      validateStep: (data) => (data.seller_name || '').trim().length > 0,
    },
    {
      id: 'sellers_agent_info',
      question: "Please provide the Seller's Agent information. Start typing a name to pick from agents you've worked with before.",
      type: 'agent_info',
      side: 'seller',
      required: true,
      showCondition: (data) => (
        isBuyerSideOnly(data)
        && Boolean(data.needs_opposing_agent_info)
        && (data.seller_name || '').trim().length > 0
      ),
      validateStep: (data) => isAgentSideComplete(data, 'seller'),
    },
    {
      id: 'offer_expiration',
      question: 'How many calendar days until this offer expires? (Expiration defaults to 5:00 p.m. on that day.)',
      type: 'text',
      placeholder: 'Enter number of days',
      required: true,
      validation: (value) => value && isPositiveIntegerDays(value)
    },
    {
      id: 'signing_date',
      question: 'Do you want to sign the document today or provide an alternative date?',
      type: 'signing_date',
      placeholder: 'Enter alternative date if preferred.',
      required: true,
      validation: (value) => isSigningDateValue(value)
    }
  ];

  useEffect(() => {
    if (!visible || !wizardOpenedRef.current || wizardStartStepResolvedRef.current) return;
    const steps = wizardStepsRef.current;
    if (!steps?.length) return;
    wizardStartStepResolvedRef.current = true;
    const seededValues = applyPaWizardDefaults({
      contract_type: 'residential_home',
      ...initialValuesRef.current,
    });
    setCurrentStep(resolveWizardStartStep(questions, steps, seededValues));
  }, [visible, questions]);

  // Skip hidden conditional steps after contractData is committed (not during render).
  useEffect(() => {
    const question = questions[currentStep];
    if (!question || isQuestionVisible(question, contractData)) {
      return undefined;
    }
    if (currentStep >= questions.length - 1) {
      return undefined;
    }
    const nextStep = resolveNextStepIndex(currentStep, contractData);
    if (nextStep !== currentStep) {
      setCurrentStep(nextStep);
    }
    return undefined;
  }, [currentStep, contractData]);

  const handleInputChange = (value) => {
    const question = questions[currentStep];
    const fieldKey = getQuestionFieldKey(question);
    setContractData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const enrichPropertyAndAdvance = async () => {
    const currentQuestion = questions[currentStep];
    if (currentQuestion.required && !isStepComplete(currentQuestion, contractData)) {
      return;
    }

    setPropertyEnriching(true);
    try {
      const payload = {
        user_email: auth0User?.email,
        property_address: contractData.property_address,
        property_address_city: contractData.property_address_city,
        property_address_state: contractData.property_address_state,
        property_address_zip_code: contractData.property_address_zip_code,
      };
      const url = formSessionToken
        ? `${FORMS_API_URL}/sessions/${formSessionToken}/enrich-property`
        : `${BACKEND_URL}/wizard/enrich-property`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      const enriched = data.enrichment;
      if (res.ok && enriched) {
        setContractData((prev) => {
          const updated = { ...prev };
          const gapFill = (key, value) => {
            if (value && !updated[key]) {
              updated[key] = value;
            }
          };
          [
            'property_address',
            'property_address_city',
            'property_address_state',
            'property_address_zip_code',
            'property_address_county',
            'year_built',
            'mls_number',
            'legal_description',
            'parcel_id',
            'seller_name',
            'purchase_price',
            'listing_price',
            'sellers_agent_name',
            'sellers_agent_email',
            'sellers_agent_phone',
            'sellers_agent_brokerage',
            'sellers_agent_brokerage_phone',
            'flood_hazard',
            'property_features',
            'property_inclusions',
            'contract_type',
            'listing_id',
          ].forEach((key) => gapFill(key, enriched[key]));
          if (enriched.listing_match_id && !updated.listing_match_id) {
            updated.listing_match_id = enriched.listing_match_id;
          }
          if (enriched.rentcast_applied) {
            updated.rentcast_applied = true;
          }
          if (!updated.acd_property_condition) {
            updated.acd_property_condition = 'rpcd';
          }
          if (!updated.acd_lbp_status) {
            const lbpSuggestion = suggestedAcdLbpStatus(updated);
            if (lbpSuggestion) {
              updated.acd_lbp_status = lbpSuggestion;
            }
          }
          if (isBuyerSideOnly(updated) && !isAgentSideComplete(updated, 'seller')) {
            updated.needs_opposing_agent_info = true;
            updated.opposing_agent_side = 'seller';
          }
          setCurrentStep(resolveNextStepIndex(currentStep, updated));
          return updated;
        });
      } else {
        setContractData((prev) => {
          const updated = { ...prev };
          if (isBuyerSideOnly(updated) && !isAgentSideComplete(updated, 'seller')) {
            updated.needs_opposing_agent_info = true;
            updated.opposing_agent_side = 'seller';
          }
          setCurrentStep(resolveNextStepIndex(currentStep, updated));
          return updated;
        });
      }
    } catch (err) {
      console.error('Property enrichment failed:', err);
      setContractData((prev) => {
        const updated = { ...prev };
        if (isBuyerSideOnly(updated) && !isAgentSideComplete(updated, 'seller')) {
          updated.needs_opposing_agent_info = true;
          updated.opposing_agent_side = 'seller';
        }
        setCurrentStep(resolveNextStepIndex(currentStep, updated));
        return updated;
      });
    } finally {
      setPropertyEnriching(false);
    }
  };

  const handleNext = () => {
    const currentQuestion = questions[currentStep];

    // Check if current question should be shown
    if (!isQuestionVisible(currentQuestion, contractData)) {
      // Skip this question and go to next
      if (currentStep < questions.length - 1) {
        setCurrentStep(resolveNextStepIndex(currentStep));
      }
      return;
    }

    if (currentQuestion.required) {
      if (currentQuestion.id === 'contract_type' && contractData.contract_type !== 'residential_home') {
        setContractTypeError(UNSUPPORTED_CONTRACT_TYPE_MESSAGE);
        return;
      }
      setContractTypeError('');
      if (!isStepComplete(currentQuestion, contractData)) {
        return;
      }
    }

    if (currentQuestion.id === 'property_address' && needsPropertyStep(contractData)) {
      enrichPropertyAndAdvance();
      return;
    }

    if (currentQuestion.id === 'wizard_client_seller' && isSellerSideOnly(contractData)) {
      const updated = {
        ...contractData,
        needs_opposing_agent_info: true,
        opposing_agent_side: 'buyer',
      };
      setContractData(updated);
      setCurrentStep(resolveNextStepIndex(currentStep, updated));
      return;
    }

    if (currentQuestion.id === 'late_seller_name') {
      const updated = {
        ...contractData,
        seller_name: (contractData.seller_name || '').trim(),
        needs_opposing_agent_info: true,
        opposing_agent_side: 'seller',
      };
      setContractData(updated);
      setCurrentStep(resolveNextStepIndex(currentStep, updated));
      return;
    }

    if (currentQuestion.id === 'opposing_client_buyer') {
      const updated = {
        ...contractData,
        needs_opposing_agent_info: true,
        opposing_agent_side: 'buyer',
      };
      setContractData(updated);
      setCurrentStep(resolveNextStepIndex(currentStep, updated));
      return;
    }

    if (currentStep < questions.length - 1) {
      setCurrentStep(resolveNextStepIndex(currentStep));
    }
  };


  const handlePrevious = () => {
    if (currentStep === 0 && onBackToCatalog) {
      onBackToCatalog();
      return;
    }
    if (currentStep > 0) {
      let prevStep = currentStep - 1;

      // Skip conditional questions when going backwards
      while (prevStep >= 0) {
        const prevQuestion = questions[prevStep];
        if (isQuestionVisible(prevQuestion, contractData)) {
          setCurrentStep(prevStep);
          return;
        }
        prevStep--;
      }
    }
  };

  const handleCreatingAgentSideSelect = (side) => {
    let updated = resetDealLinkFields({
      ...contractData,
      creating_agent_side: side,
      needs_creating_agent_side_choice: false,
    });
    if (side === 'dual') {
      updated = applyCreatingAgentProfileFromUser(updated, 'buyer', userProfile);
      updated = applyCreatingAgentProfileFromUser(updated, 'seller', userProfile);
      updated.needs_opposing_agent_info = false;
    } else {
      updated = applyCreatingAgentProfileFromUser(updated, side, userProfile);
    }
    setContractData(updated);
    if (currentStep < questions.length - 1) {
      setCurrentStep(resolveNextStepIndex(currentStep, updated));
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const incomplete = questions.find((question) => {
        if (!isQuestionVisible(question, contractData)) return false;
        return question.required && !isStepComplete(question, contractData);
      });
      if (incomplete) {
        const idx = questions.indexOf(incomplete);
        if (idx >= 0) setCurrentStep(idx);
        setIsSubmitting(false);
        return;
      }

      let side = contractData.creating_agent_side;
      if (!hasCreatingAgentSide(contractData)) {
        const sideIdx = questions.findIndex((question) => question.id === 'creating_agent_side');
        if (sideIdx >= 0) setCurrentStep(sideIdx);
        setIsSubmitting(false);
        return;
      }
      if (isDualAgency(contractData)) {
        side = 'dual';
      } else if (!side) {
        side = 'seller';
      }
      const profileFromDeal = (prefix) => ({
        name: contractData[`${prefix}_name`] || userProfile?.name,
        license_number: contractData[`${prefix}_license`] || userProfile?.license_number,
        email: contractData[`${prefix}_email`] || userProfile?.email,
        phone_number: contractData[`${prefix}_phone`] || userProfile?.phone_number,
        licensed_state: userProfile?.licensed_state,
        brokerage_company_name:
          contractData[`${prefix}_brokerage`] || userProfile?.brokerage_company_name,
        company_address:
          contractData[`${prefix}_brokerage_address`] || userProfile?.company_address,
        company_license_number:
          contractData[`${prefix}_brokerage_license`] || userProfile?.company_license_number,
        brokerage_phone:
          contractData[`${prefix}_brokerage_phone`] || userProfile?.phone_number,
      });
      const sellerProfile = profileFromDeal('sellers_agent');
      const buyerProfile = profileFromDeal('buyer_agent');
      const contractDataWithProfile = {
        ...contractData,
        creating_agent_side: side,
        property_address_state:
          contractData.property_address_state || userProfile?.licensed_state || 'OK',
        ...(side === 'seller'
          ? { agent_profile: sellerProfile }
          : side === 'buyer'
            ? { buyer_agent_profile: buyerProfile }
            : { agent_profile: sellerProfile, buyer_agent_profile: buyerProfile }),
      };

      const response = formSessionToken
        ? await submitFormSession(formSessionToken, contractDataWithProfile)
        : await fetch(`${BACKEND_URL}/create-new-contract`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(contractDataWithProfile)
        }).then(r => r.json().then(data => ({ ok: r.ok, data })));

      const result = formSessionToken ? response : response.data;
      const ok = formSessionToken ? response.success : response.ok;

      if (ok) {
        setSubmitResult({
          success: true,
          message: 'Contract created successfully!',
          data: formSessionToken
            ? {
              contract_id: result.entity_id,
              property_address: result.summary?.property_address || contractData.property_address,
              buyer_name: result.summary?.buyer_name || contractData.buyer_name,
              seller_name: result.summary?.seller_name || contractData.seller_name,
              purchase_price: result.summary?.purchase_price || contractData.purchase_price,
            }
            : result
        });
        if (auth0User?.email) {
          trackFormWizardEvent(getAccessTokenSilently, formWizardEventPayload({
            phase: 'completed',
            formType: 'purchase_agreement',
            userEmail: auth0User.email,
            userName: auth0User.name || auth0User.nickname || '',
            metadata: {
              contract_id: formSessionToken ? result.entity_id : result.contract_id,
              property_address: contractData.property_address,
            },
          })).catch(() => { });
        }
      } else {
        setSubmitResult({ success: false, message: (result.error || result.message) || 'Failed to create contract' });
      }
    } catch (error) {
      setSubmitResult({
        success: false,
        message: formSessionToken
          ? getFormSessionErrorMessage(error, 'Failed to create contract')
          : `Network error: ${error.message || 'Unknown error'}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderInput = () => {
    const question = questions[currentStep];
    const fieldKey = getQuestionFieldKey(question);
    const value = contractData[fieldKey] || '';

    if (!isQuestionVisible(question, contractData)) {
      return null;
    }

    switch (question.type) {
      case 'contract_type':
        return (
          <div className="wizard-contract-type">
            {CONTRACT_TYPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`wizard-button ${contractData.contract_type === option.value ? 'active' : ''}`}
                onClick={() => {
                  handleInputChange(option.value);
                  if (option.value === 'residential_home') {
                    setContractTypeError('');
                  } else {
                    setContractTypeError(UNSUPPORTED_CONTRACT_TYPE_MESSAGE);
                  }
                }}
              >
                {option.label}
              </button>
            ))}
            {contractTypeError && (
              <p className="wizard-inline-error">{contractTypeError}</p>
            )}
          </div>
        );
      case 'address':
        return (
          <PropertyAddressFields
            values={contractData}
            onChange={(field, value) => {
              if (field === 'property_address') {
                handleInputChange(value);
                return;
              }
              setContractData((prev) => ({ ...prev, [field]: value }));
            }}
          />
        );
      case 'currency':
        return (
          <div className="wizard-currency">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              value={value}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={question.placeholder}
              className="wizard-input currency-input"
            />
          </div>
        );
      case 'earnest_money':
        return (
          <div className="wizard-earnest-money">
            <button
              className={`wizard-button ${value === getEarnestMoneyAmount() ? 'active' : ''}`}
              onClick={() => {
                commitAnswerAndAdvance(question.id, getEarnestMoneyAmount());
              }}
            >
              3%
            </button>
            <div className="wizard-input-group">
              <input
                type="text"
                value={value}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                className="wizard-input"
              />
            </div>
          </div>
        );
      case 'closing_days':
        return (
          <div className="wizard-closing-days">
            <button
              type="button"
              className={`wizard-button wizard-button-multiline ${value === '30' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, '30')}
            >
              <span className="wizard-button-line">30 days</span>
              <span className="wizard-button-line">({formatLongDate(getStandardClosingDate())})</span>
            </button>
            <div className="wizard-input-group">
              <input
                type="text"
                value={value}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                className="wizard-input"
              />
            </div>
          </div>
        );
      case 'inclusions_exclusions':
        return (
          <div className="wizard-inclusions-exclusions">
            <button
              className={`wizard-button ${value === 'No' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, 'No')}
            >
              No
            </button>
            <div className="wizard-input-group">
              <textarea
                value={value}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                className="wizard-textarea"
                rows={4}
              />
            </div>
          </div>
        );
      case 'contingency_start':
        return (
          <div className="wizard-contingency-start">
            <button
              className={`wizard-button ${value === '3' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, '3')}
            >
              3 days
            </button>
            <div className="wizard-input-group">
              <input
                type="text"
                value={value}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                className="wizard-input"
              />
            </div>
          </div>
        );
      case 'inspection_days':
        return (
          <div className="wizard-inspection-days">
            <button
              className={`wizard-button ${value === '10' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, '10')}
            >
              10 days
            </button>
            <div className="wizard-input-group">
              <input
                type="number"
                value={value}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                className="wizard-input"
              />
            </div>
          </div>
        );
      case 'access_persons':
        return (
          <div className="wizard-access-persons">
            <button
              className={`wizard-button ${value === 'No' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, 'No')}
            >
              No
            </button>
            <div className="wizard-input-group">
              <textarea
                value={value}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                className="wizard-textarea"
                rows={4}
              />
            </div>
          </div>
        );
      case 'negotiation_days':
        return (
          <div className="wizard-negotiation-days">
            <button
              className={`wizard-button ${value === '7' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, '7')}
            >
              7 days
            </button>
            <div className="wizard-input-group">
              <input
                type="number"
                value={value}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                className="wizard-input"
              />
            </div>
          </div>
        );
      case 'survey_mortgage_payer':
        return (
          <div className="wizard-survey-mortgage-payer">
            {question.options.map(option => (
              <button
                key={option}
                className={`wizard-button ${value === option ? 'active' : ''}`}
                onClick={() => commitAnswerAndAdvance(question.id, option)}
              >
                {option}
              </button>
            ))}
          </div>
        );
      case 'acd_property_condition':
        return (
          <div className="wizard-acd-disclosures">
            {ACD_PROPERTY_CONDITION_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`wizard-button wizard-button-multiline ${value === option.value ? 'active' : ''}`}
                onClick={() => commitAnswerAndAdvance(question.id, option.value)}
              >
                <span className="wizard-button-line">{option.label}</span>
              </button>
            ))}
          </div>
        );
      case 'acd_lbp_status':
        return (
          <div className="wizard-acd-disclosures">
            {ACD_LBP_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`wizard-button wizard-button-multiline ${value === option.value ? 'active' : ''}`}
                onClick={() => commitAnswerAndAdvance(question.id, option.value)}
              >
                <span className="wizard-button-line">{option.label}</span>
              </button>
            ))}
          </div>
        );
      case 'title_extension':
        return (
          <div className="wizard-title-extension">
            <button
              className={`wizard-button ${value === '30' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, '30')}
            >
              30 days
            </button>
            <div className="wizard-input-group">
              <input
                type="number"
                value={value}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                className="wizard-input"
              />
            </div>
          </div>
        );
      case 'additional_provisions':
        return (
          <div className="wizard-additional-provisions">
            <button
              className={`wizard-button ${value === 'No' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, 'No')}
            >
              No
            </button>
            <div className="wizard-input-group">
              <textarea
                value={value}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                className="wizard-textarea"
                rows={4}
              />
            </div>
          </div>
        );
      case 'home_warranty_buyer_request':
        return (
          <div className="wizard-home-warranty-buyer">
            <button
              type="button"
              className={`wizard-button ${(value || '').toLowerCase() === 'no' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, 'no')}
            >
              No
            </button>
            <div className="wizard-input-group">
              <input
                type="text"
                value={(value || '').toLowerCase() === 'no' ? '' : value}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                className="wizard-input"
              />
            </div>
          </div>
        );
      case 'creating_agent_side':
        return (
          <div className="wizard-creating-agent-side">
            <button
              type="button"
              className={`wizard-button ${contractData.creating_agent_side === 'buyer' ? 'active' : ''}`}
              onClick={() => handleCreatingAgentSideSelect('buyer')}
            >
              Buyer
            </button>
            <button
              type="button"
              className={`wizard-button ${contractData.creating_agent_side === 'seller' ? 'active' : ''}`}
              onClick={() => handleCreatingAgentSideSelect('seller')}
            >
              Seller
            </button>
            <button
              type="button"
              className={`wizard-button ${contractData.creating_agent_side === 'dual' ? 'active' : ''}`}
              onClick={() => handleCreatingAgentSideSelect('dual')}
            >
              Both
            </button>
          </div>
        );
      case 'wizard_client':
        return (
          <WizardClientPicker
            clientSide={question.clientSide}
            contractData={contractData}
            setContractData={setContractData}
            formSessionToken={formSessionToken}
            userEmail={auth0User?.email}
            mode="deal"
            placeholder={
              question.clientSide === 'buyer'
                ? 'Search fully executed buyer agreements'
                : 'Search your active listings'
            }
            freeTextField={question.clientSide === 'buyer' ? 'buyer_name' : 'seller_name'}
          />
        );
      case 'agent_info':
        return (
          <OpposingAgentInfoFields
            side={question.side || 'buyer'}
            contractData={contractData}
            setContractData={setContractData}
            formSessionToken={formSessionToken}
            userProfile={userProfile}
          />
        );
      case 'signing_date':
        return (
          <div className="wizard-signing-date">
            <button
              type="button"
              className={`wizard-button ${value === getEffectiveDate() ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, getEffectiveDate())}
            >
              Yes. Today ({formatLongDate(getEffectiveDate())}).
            </button>
            <div className="wizard-input-group">
              <input
                type="text"
                value={value}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={question.placeholder}
                className="wizard-input"
              />
            </div>
          </div>
        );
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={question.placeholder}
            className="wizard-textarea"
            rows={4}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={question.placeholder}
            className="wizard-input"
          />
        );
      case 'yesno':
        return (
          <div className="wizard-yesno">
            <button
              className={`wizard-button ${value === 'yes' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, 'yes')}
            >
              Yes
            </button>
            <button
              className={`wizard-button ${value === 'no' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, 'no')}
            >
              No
            </button>
          </div>
        );
      case 'mortgage_survey_report':
        return (
          <div className="wizard-mortgage-survey-report">
            <button
              className={`wizard-button ${value === 'mortgage report' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, 'mortgage report')}
            >
              Mortgage Inspection Report
            </button>
            <button
              className={`wizard-button ${value === 'survey report' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, 'survey report')}
            >
              Boundary Survey
            </button>
            <button
              className={`wizard-button ${value === 'no report' ? 'active' : ''}`}
              onClick={() => commitAnswerAndAdvance(question.id, 'no report')}
            >
              Neither
            </button>
          </div>
        );
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(e.target.value)}
            className="wizard-select"
          >
            <option value="">Select an option</option>
            {question.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={question.placeholder}
            className="wizard-input"
          />
        );
    }
  };

  if (!visible) return null;

  const currentQuestion = questions[currentStep];

  if (submitResult) {
    const closeAfterSubmit = () => {
      const closeResult = purchaseAgreementCloseResult(submitResult);
      if (closeResult) {
        onClose(closeResult);
        return;
      }
      onClose();
    };
    return (
      <div className={isPageMode ? 'wizard-overlay wizard-page-mode' : 'wizard-overlay'}>
        <div className="wizard-container">
          <div className="wizard-header">
            <h2>Contract Creation Result</h2>
            <button className="wizard-close" onClick={closeAfterSubmit}>×</button>
          </div>
          <div className="wizard-content">
            <div className={`result-message ${submitResult.success ? 'success' : 'error'}`}>
              {submitResult.success ? '✅' : '❌'} {submitResult.message}
            </div>
            {submitResult.success && submitResult.data && (
              <div className="result-details">
                <p><strong>Property:</strong> {submitResult.data.property_address}</p>
                {submitResult.data.buyer_name && (
                  <p><strong>Buyer:</strong> {submitResult.data.buyer_name}</p>
                )}
                {submitResult.data.seller_name && (
                  <p><strong>Seller:</strong> {submitResult.data.seller_name}</p>
                )}
                {submitResult.data.purchase_price != null && submitResult.data.purchase_price !== '' && (
                  <p><strong>Price:</strong> {formatWizardPurchasePrice(submitResult.data.purchase_price)}</p>
                )}
              </div>
            )}
            <div className="wizard-actions">
              <button className="wizard-button primary" onClick={closeAfterSubmit}>
                {submitResult.success ? 'View contract' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const visibleQuestions = getVisibleQuestions();
  const isLastStep = currentStep === questions.length - 1;

  // Check if current question should be shown
  const shouldShowCurrentQuestion = isQuestionVisible(currentQuestion, contractData);

  const canProceed = !shouldShowCurrentQuestion || !currentQuestion.required
    || isStepComplete(currentQuestion, contractData);
  const overlayClass = isPageMode ? 'wizard-overlay wizard-page-mode' : 'wizard-overlay';

  const actionButtons = (
    <>
      <button
        className="wizard-button secondary"
        onClick={handlePrevious}
        disabled={currentStep === 0 && !onBackToCatalog}
      >
        Previous
      </button>
      {isLastStep ? (
        <button
          className={WIZARD_PRIMARY_BUTTON_CLASS}
          onClick={handleSubmit}
          disabled={!canProceed || isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create Contract'}
        </button>
      ) : (
        <button
          className={WIZARD_PRIMARY_BUTTON_CLASS}
          onClick={handleNext}
          disabled={!canProceed || propertyEnriching}
        >
          {propertyEnriching ? 'Looking up property…' : 'Next'}
        </button>
      )}
    </>
  );

  return (
    <div className={overlayClass}>
      <div className="wizard-container">
        <div className="wizard-header">
          <h2>Create New Contract</h2>
          <button className="wizard-close" onClick={onClose}>×</button>
        </div>

        <WizardProgress current={currentStep + 1} total={questions.length} />

        <div className="wizard-content">
          <WizardStepReady ready={stepSeeded && profileReady}>
            <div className="wizard-question">
              <h3>{getQuestionText(currentQuestion, contractData)}</h3>
              {currentQuestion.id === 'property_inclusions' && contractData.property_features && (
                <p className="wizard-property-features">
                  RentCast listed features (display only): {contractData.property_features}
                </p>
              )}
              {renderInput()}
            </div>

            {!isPageMode && (
              <div className="wizard-actions">{actionButtons}</div>
            )}
          </WizardStepReady>
        </div>
        {isPageMode && stepSeeded && profileReady && (
          <div className="wizard-sticky-footer">
            {actionButtons}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractWizard;
